import React from 'react';
import styled, {css} from 'styled-components';
import {ButtonVariant, Container as Button} from 'components/Button';
import {transition} from 'helpers/css';
import {PreviewObject} from 'types';
import {getFilePath} from 'api/library';

export interface UserAvatarProps {
  preview?: PreviewObject;
  editable?: boolean;
  onClickLoad?: () => void;
  onClickDelete?: () => void;
}

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const EmptyAvatar = styled.div<Pick<UserAvatarProps, 'editable'>>`
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg width='162' height='162' viewBox='0 0 162 162' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M5 0C2.23858 0 0 2.23857 0 5V157C0 159.761 2.23857 162 5 162H157C159.761 162 162 159.761 162 157V5C162 2.23858 159.761 0 157 0H5ZM80.7344 23C95.5354 23 107.529 34.9979 107.529 49.7924C107.529 64.5906 95.5354 76.5849 80.7344 76.5849C65.9405 76.5849 53.9467 64.5906 53.9467 49.7924C53.9467 34.9979 65.9405 23 80.7344 23ZM80.7166 139.602C65.9547 139.602 52.4346 134.226 42.0062 125.327C39.4659 123.16 38 119.983 38 116.649C38 101.645 50.1432 89.636 65.1506 89.636H96.3253C111.336 89.636 123.433 101.645 123.433 116.649C123.433 119.987 121.974 123.157 119.43 125.324C109.006 134.226 95.482 139.602 80.7166 139.602Z' fill='%234B4B4B'/%3E%3C/svg%3E%0A");
  background-position: 50%;
  background-repeat: no-repeat;
  background-size: 100%;
  
  ${transition()}
  
  ${({editable}) => {
    return editable && css`
        cursor: pointer;
        
        &:hover {
          opacity: .6;
        }
      `;
  }
}};
`;

const AvatarOptions = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 0 21px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  background: linear-gradient(
      0deg, 
      rgba(35, 35, 35, 0.9), 
      rgba(35, 35, 35, 0.9)
  );
  opacity: 0;
  pointer-events: none;

  ${transition()}
`;

const AvatarButton = styled(Button)`
  padding: 10px;
  border-radius: 5px;
  cursor: pointer;
  color: #fff;
`;

export const Wrapper = styled.div<Pick<UserAvatarProps, 'editable'>>`
  position: relative;
  border-radius: 5px;
  overflow: hidden;

  ${({editable}) => {
    if (editable) {
      return css`
        width: 162px;
        height: 162px;
      `;
    } else {
      return css`
        width: 200px;
        height: 200px;
      `;
    }
  }};
  
  &:hover {
    ${AvatarOptions} {
      opacity: 1;
      pointer-events: auto;
    }
  }
  
  ${AvatarButton} {
    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }
`;

/**
 * The component render user avatar
 * @param {UserAvatarProps} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function UserAvatar(
    props: UserAvatarProps,
): React.ReactElement {
  const {editable, preview, onClickLoad, onClickDelete} = props;

  const previewSrc = preview?.id && getFilePath(preview.id) || '';

  /**
   * The function handles the click on the avatar upload button
   */
  function onUploadAvatarClick() {
    if (editable) {
      onClickLoad && onClickLoad();
    }
  }

  /**
   * The function handles the click on the avatar delete button
   */
  function onDeleteAvatarClick() {
    if (editable) {
      onClickDelete && onClickDelete();
    }
  }

  /**
   * The function render avatar
   * @return {JSX.Element}
   */
  function renderAvatar() {
    return previewSrc
      ? <Avatar src={previewSrc}/>
      : (
          <EmptyAvatar
            editable={editable}
            onClick={onUploadAvatarClick}
          />
        );
  }

  /**
   * The function render avatar options
   * @return {JSX.Element | void}
   */
  function renderAvatarOptions() {
    if (editable && previewSrc) {
      return (
        <AvatarOptions>
          <AvatarButton
            variant={ButtonVariant.Outline}
            stretched
            onClick={onUploadAvatarClick}
          >Заменить</AvatarButton>
          <AvatarButton
            variant={ButtonVariant.Outline}
            stretched
            onClick={onDeleteAvatarClick}
          >Удалить</AvatarButton>
        </AvatarOptions>
      );
    }
  }

  return (
    <Wrapper editable={editable} >
      {renderAvatar()}
      {renderAvatarOptions()}
    </Wrapper>
  );
};
